import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Building2, Home, TrendingDown, Handshake, Briefcase, MessageSquare } from "lucide-react";
import { useI18n } from "../../lib/i18n";

/* Icon lookup by form key */
const ICONS = {
  construction: Building2,
  buy: Home,
  sell: TrendingDown,
  partnership: Handshake,
  career: Briefcase,
};

/**
 * Fırat brand CSS overrides — injected inside each iframe so all Bitrix
 * forms match our design system while keeping them isolated per tab.
 */
const IFRAME_BRAND_CSS = `
  html, body {
    margin:0;
    padding:0;
    background:transparent;
    height:auto !important;
    min-height:0 !important;
    overflow:hidden !important;
  }
  body {
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    color: #3A3A3A;
    padding: 4px;
    overflow-x: hidden;
  }
  /* Prevent any Bitrix element from stretching to fill the iframe viewport,
     which caused a feedback loop with our auto-resize logic. */
  .b24-form, .b24-form-wrapper, .b24-form-content, .b24-form-body,
  .b24-form-inner, .b24-window, .b24-window-scrollable,
  .b24-form-content > div, [class*="b24-form"] {
    height: auto !important;
    min-height: 0 !important;
    max-height: none !important;
  }
  .b24-form, .b24-form-wrapper, .b24-form-content,
  .b24-window-scrollable, .b24-form-body, .b24-form-inner {
    background: transparent !important;
    box-shadow: none !important;
    padding: 0 !important;
    margin: 0 !important;
    border: none !important;
    max-width: 100% !important;
  }
  .b24-form-header, .b24-form-header-title, .b24-form-header-description,
  .b24-form-footer-info, .b24-form-sign-block, .b24-form-sign,
  .b24-form-sign-content, .b24-form-sign-container, .b24-form-copyright,
  [class*="b24-form-sign"], [class*="b24-copyright"], [class*="b24-brand"],
  a[href*="bitrix24"], a[href*="bitrix"], .b24-widget-branding { display: none !important; }

  .b24-form-field, .b24-form-field-item { margin-bottom: 1.15rem !important; }
  .b24-form-control, input[type=text], input[type=email], input[type=tel],
  input[type=number], input[type=url], textarea, select {
    background: transparent !important;
    border: none !important;
    border-bottom: 1px solid rgba(0,0,0,0.14) !important;
    border-radius: 0 !important;
    padding: 0.85rem 0 !important;
    font-family: 'Inter', sans-serif !important;
    font-size: 0.95rem !important;
    color: #3A3A3A !important;
    box-shadow: none !important;
    transition: border-color 300ms ease !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  .b24-form-control:focus, input:focus, textarea:focus, select:focus {
    border-bottom-color: #22346B !important;
    outline: none !important;
    box-shadow: none !important;
  }
  textarea { min-height: 96px !important; resize: vertical !important; }

  .b24-form-control-label, label {
    font-family: 'Inter', sans-serif !important;
    font-size: 0.68rem !important;
    letter-spacing: 0.24em !important;
    text-transform: uppercase !important;
    color: #8B8D93 !important;
    font-weight: 500 !important;
    margin-bottom: 0.25rem !important;
    display: block !important;
  }

  .b24-form-check-label, .b24-form-agreement-content, .b24-form-agreement {
    font-size: 0.78rem !important;
    color: #8B8D93 !important;
    line-height: 1.5 !important;
    letter-spacing: 0 !important;
    text-transform: none !important;
    font-weight: 400 !important;
  }
  .b24-form-check-decor, .b24-form-checkbox-decor {
    border-color: #22346B !important;
    background: transparent !important;
  }
  .b24-form-check-decor.b24-form-check-decor-active,
  .b24-form-checkbox-decor.b24-form-checkbox-decor-active {
    background: #22346B !important;
    border-color: #22346B !important;
  }

  .b24-form-btn, button[type="submit"], .b24-form-btn-container button {
    background: #1A2A5A !important;
    color: #fff !important;
    border: 1px solid #1A2A5A !important;
    border-radius: 0 !important;
    padding: 0.95rem 2.25rem !important;
    font-family: 'Inter', sans-serif !important;
    font-size: 0.72rem !important;
    letter-spacing: 0.24em !important;
    text-transform: uppercase !important;
    font-weight: 500 !important;
    transition: all 300ms ease !important;
    cursor: pointer !important;
    box-shadow: 0 8px 24px rgba(26,42,90,0.15) !important;
  }
  .b24-form-btn:hover, button[type="submit"]:hover {
    background: #2E4590 !important;
    border-color: #2E4590 !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 12px 30px rgba(46,69,144,0.25) !important;
  }

  .b24-form-success-title { color: #1A2A5A !important; font-family: 'Cormorant Garamond', serif !important; }
  .b24-form-success-text { color: #8B8D93 !important; }
  .b24-form-control-alert, .b24-form-field-error { color: #b91c1c !important; font-size: 0.75rem !important; }
`;

/* Builds the srcdoc HTML for a given Bitrix form config */
const buildSrcDoc = (form, parentOrigin = "*") => {
  const cacheBuster = Math.floor(Date.now() / 180000);
  const loader = form.loader_url.includes("?")
    ? `${form.loader_url}&_=${cacheBuster}`
    : `${form.loader_url}?${cacheBuster}`;
  const originLiteral = JSON.stringify(String(parentOrigin || "*"));
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<base target="_top" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=Inter:wght@300;400;500;600&display=swap" />
<style>${IFRAME_BRAND_CSS}</style>
</head>
<body>
<script data-b24-form="${form.form_id}" data-skip-moving="true"></script>
<script>
(function(w,d,u){
  var s=d.createElement('script');s.async=true;s.src=u;
  var h=d.getElementsByTagName('script')[0];h.parentNode.insertBefore(s,h);
})(window,document,'${loader}');

/* Auto-resize: post height to parent whenever content changes.
   Uses the actual bottom position of the last visible child so the iframe
   never re-inflates itself into a feedback loop with the parent height. */
(function(){
  var lastH = 0;
  function scrub(){
    var sel = 'a[href*="bitrix24"], a[href*="bitrix"], .b24-widget-branding, [class*="b24-form-sign"], [class*="b24-copyright"], [class*="b24-brand"], .b24-form-sign-block, .b24-form-sign';
    document.querySelectorAll(sel).forEach(function(el){ el.style.display='none'; el.remove(); });
  }
  function measure(){
    /* Find the bottom-most visible descendant of body — this is the real
       content height, independent of the iframe's current viewport. */
    var maxBottom = 0;
    var els = document.body.querySelectorAll('*');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (!el.offsetParent && el !== document.body) continue;
      var r = el.getBoundingClientRect();
      if (r.bottom > maxBottom) maxBottom = r.bottom;
    }
    return Math.min(2400, Math.max(120, Math.ceil(maxBottom)));
  }
  function post(){
    scrub();
    var h = measure();
    if (Math.abs(h - lastH) < 6) return; /* ignore sub-6px jitter */
    lastH = h;
    parent.postMessage({ __firatBitrixHeight: h }, ${originLiteral});
  }
  var mo = new MutationObserver(post);
  mo.observe(document.body, { childList: true, subtree: true, attributes: true });
  window.addEventListener('load', post);
  setInterval(post, 1200);
})();
</script>
</body>
</html>`;
};

const BitrixContactForm = ({ forms = [] }) => {
  const { pick, lang } = useI18n();
  const iframeRef = useRef(null);
  const [frameHeight, setFrameHeight] = useState(520);

  const sorted = useMemo(
    () =>
      [...forms]
        .filter((f) => f?.form_id && f?.loader_url)
        .sort((a, b) => (a.order || 0) - (b.order || 0)),
    [forms]
  );

  const defaultIdx = Math.max(0, sorted.findIndex((f) => f.is_default));
  const [idx, setIdx] = useState(defaultIdx);

  useEffect(() => {
    const d = sorted.findIndex((f) => f.is_default);
    setIdx(d >= 0 ? d : 0);
  }, [sorted.length]);

  const active = sorted[idx];

  /* Listen for iframe height messages — capped and clamped to prevent runaway growth */
  useEffect(() => {
    const onMsg = (e) => {
      // Verify origin — srcdoc iframes report origin "null"; also accept our own origin for dev.
      if (e.origin !== "null" && e.origin !== window.location.origin) return;
      if (iframeRef.current && e.source !== iframeRef.current.contentWindow) return;
      const h = e?.data?.__firatBitrixHeight;
      if (typeof h !== "number" || h <= 100 || h >= 2400) return;
      setFrameHeight((prev) => {
        const target = Math.min(2400, h + 8);
        /* only apply if change is meaningful — kills jitter feedback loops */
        if (Math.abs(target - prev) < 8) return prev;
        return target;
      });
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  /* Reset height when switching forms so a taller previous form does not
     leak into the next one. */
  useEffect(() => {
    setFrameHeight(520);
  }, [active?.form_id]);

  const srcDoc = useMemo(() => (
    active ? buildSrcDoc(active, typeof window !== "undefined" ? window.location.origin : "*") : ""
  ), [active]);

  if (!sorted.length) {
    return (
      <div className="w-full border border-black/10 p-6 md:p-8 bg-brand-light text-sm text-brand-medium">
        {pick({
          tr: "Henüz iletişim formu tanımlanmamış. Lütfen Site Ayarları → Bitrix24 Formları bölümünden ekleyin.",
          en: "No contact forms configured yet.",
          ar: "لم يتم إعداد أي نموذج اتصال بعد.",
        })}
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="bitrix-contact-form" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Eyebrow */}
      <div className="flex items-center gap-3 mb-4">
        <span className="h-px w-8 bg-brand-primary" />
        <span className="text-[0.62rem] md:text-[0.65rem] tracking-[0.32em] uppercase text-brand-primary font-medium">
          {pick({ tr: "Konu Seçin", en: "Select a topic", ar: "اختر موضوعاً" })}
        </span>
      </div>

      {/* Topic cards */}
      <div
        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-3 mb-8"
        role="tablist"
        data-testid="bitrix-topic-tabs"
      >
        {sorted.map((f, i) => {
          const on = i === idx;
          const Icon = ICONS[f.key] || MessageSquare;
          return (
            <button
              type="button"
              role="tab"
              aria-selected={on}
              key={f.key || i}
              onClick={() => setIdx(i)}
              className={`relative overflow-hidden group px-3 md:px-4 py-4 md:py-5 border transition-all duration-300 text-left ${
                on
                  ? "bg-brand-navy border-brand-navy text-white shadow-[0_10px_30px_rgba(26,42,90,0.25)]"
                  : "bg-white border-black/10 text-brand-dark hover:border-brand-primary hover:-translate-y-0.5"
              }`}
              data-testid={`bitrix-tab-${f.key || i}`}
            >
              {on ? (
                <span className="absolute inset-0 pointer-events-none bg-gradient-to-br from-white/10 to-transparent" />
              ) : null}
              <div className="relative flex flex-col gap-2">
                <span
                  className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                    on
                      ? "bg-white/15 text-white"
                      : "bg-brand-primary/10 text-brand-primary group-hover:bg-brand-primary group-hover:text-white"
                  }`}
                >
                  <Icon size={16} strokeWidth={1.5} />
                </span>
                <span
                  className={`text-[0.66rem] md:text-[0.7rem] tracking-[0.16em] uppercase font-medium leading-tight ${
                    on ? "text-white" : "text-brand-navy"
                  }`}
                >
                  {pick(f.label) || f.key || `Form ${i + 1}`}
                </span>
              </div>
              {on ? (
                <motion.span
                  layoutId="bitrix-tab-underline"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-gold-bright"
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Isolated iframe host — a new iframe per active form guarantees fresh Bitrix load */}
      <div className="relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={active?.form_id || idx}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.35 }}
          >
            <iframe
              ref={iframeRef}
              title={`bitrix-${active?.key || idx}`}
              srcDoc={srcDoc}
              className="w-full block bg-transparent"
              style={{ height: frameHeight, border: "0" }}
              data-testid="bitrix-form-iframe"
              sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-top-navigation-by-user-activation"
            />
          </motion.div>
        </AnimatePresence>
      </div>

      <p className="mt-6 text-xs text-brand-medium leading-relaxed">
        {pick({
          tr: "Formu göndererek KVKK & Gizlilik Politikamızı kabul etmiş sayılırsınız. Bilgileriniz güvende ve yalnızca size ulaşmak için kullanılır.",
          en: "By submitting the form you accept our Privacy Policy. Your details are secure and only used to contact you back.",
          ar: "بإرسال هذا النموذج فأنت توافق على سياسة الخصوصية.",
        })}
      </p>
    </div>
  );
};

export default BitrixContactForm;
